import { useQuery } from '@tanstack/react-query'
import { ExternalLink, FilePen, Settings } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router'
import { EmptyState } from '@/components/feedback/States'
import { DataTable, type Column } from '@/components/tables/DataTable'
import { FilterBar, SearchInput } from '@/components/tables/FilterBar'
import { Pagination } from '@/components/tables/Pagination'
import { Alert } from '@/components/ui/Alert'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { PageHeader } from '@/components/ui/PageHeader'
import { Select } from '@/components/ui/Select'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/Tabs'
import { admissionSettingsQuery, applicationCountsQuery, applicationsQuery } from '@/features/admissions/api'
import { admissionStatusLabel, admissionStatusTone } from '@/features/admissions/status'
import { ADMISSION_STATUSES, type ApplicationSummary } from '@/features/admissions/types'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { useListParams } from '@/hooks/useListParams'
import { formatDateTime } from '@/utils/format'

const FILTERS = ['status', 'classId'] as const

/** Applications received through the public form, by status. */
export function AdmissionsListPage() {
  const { t } = useTranslation('admissions')
  useDocumentTitle('Online admissions')
  const navigate = useNavigate()
  const { state, update, setPage } = useListParams(FILTERS)
  // Newest first (the API's default order).
  const list = useQuery(
    applicationsQuery({
      page: state.page,
      perPage: state.perPage,
      search: state.search || undefined,
      ...state.filters,
    }),
  )
  const counts = useQuery(applicationCountsQuery({ classId: state.filters.classId }))
  const settings = useQuery(admissionSettingsQuery())
  const total = counts.data ? Object.values(counts.data).reduce((a, b) => a + b, 0) : undefined
  const status = state.filters.status ?? 'all'

  const columns: Column<ApplicationSummary>[] = [
    {
      key: 'name',
      header: t('Applicant'),
      cell: (a) => (
        <div className="min-w-0">
          <Link
            to={`/admin/admissions/${a.id}`}
            onClick={(e) => e.stopPropagation()}
            className="block truncate font-medium text-ink hover:text-brand-700"
          >
            {a.fullName}
          </Link>
          <span className="block font-mono text-xs text-muted">{a.reference}</span>
        </div>
      ),
    },
    {
      key: 'class',
      header: t('Class'),
      cell: (a) => (
        <span>
          {a.className}
          <span className="block text-xs text-muted">{a.academicYear.name}</span>
        </span>
      ),
    },
    {
      key: 'guardian',
      header: t('Contact'),
      hideBelow: 'lg',
      cell: (a) =>
        a.guardian ? (
          <div className="min-w-0">
            <span className="block truncate">{a.guardian.name}</span>
            <span className="block text-xs text-muted">{a.guardian.phone ?? a.guardian.email ?? ''}</span>
          </div>
        ) : (
          (a.phone ?? '—')
        ),
    },
    { key: 'submitted', header: t('Sent'), hideBelow: 'md', cell: (a) => formatDateTime(a.submittedAt) },
    {
      key: 'status',
      header: t('Status'),
      cell: (a) => (
        <Badge tone={admissionStatusTone[a.status]} dot size="sm">
          {t(admissionStatusLabel[a.status])}
        </Badge>
      ),
    },
  ]

  return (
    <>
      <PageHeader
        title={t('Online admissions')}
        description={t(
          'Applications sent by families through the online form: review them, decide, then enrol the student in a section.',
        )}
        breadcrumbs={[{ label: t('People') }, { label: t('Online admissions') }]}
        homeHref="/admin"
        actions={
          <>
            {settings.data ? (
              <Button variant="secondary" leftIcon={<ExternalLink />} asChild>
                <a href={settings.data.publicUrl} target="_blank" rel="noreferrer">
                  {t('Open the form')}
                </a>
              </Button>
            ) : null}
            <Button variant="secondary" leftIcon={<Settings />} asChild>
              <Link to="/admin/admissions/settings">{t('Settings')}</Link>
            </Button>
          </>
        }
      />
      {settings.data ? (
        <Alert tone={settings.data.isOpenNow ? 'success' : 'warning'} className="mb-4">
          {settings.data.isOpenNow
            ? t('The form is open for {{year}}.', { year: settings.data.academicYear?.name ?? '' })
            : t('The form is closed: families cannot apply at the moment.')}
        </Alert>
      ) : null}
      <Card>
        <Tabs value={status} onValueChange={(v) => update({ status: v === 'all' ? undefined : v })}>
          <TabsList className="px-3" aria-label={t('Filter by status')}>
            <TabsTrigger value="all" count={total}>
              {t('All')}
            </TabsTrigger>
            {ADMISSION_STATUSES.map((s) => (
              <TabsTrigger key={s} value={s} count={counts.data?.[s]}>
                {t(admissionStatusLabel[s])}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <FilterBar
          actions={
            state.search || state.filters.classId ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => update({ search: undefined, classId: undefined })}
              >
                {t('Clear filters')}
              </Button>
            ) : null
          }
        >
          <SearchInput
            value={state.search}
            onChange={(v) => update({ search: v })}
            placeholder={t('Name, reference, parent or phone')}
            label={t('Search applications')}
          />
          <Select
            aria-label={t('Filter by class')}
            className="sm:w-auto sm:min-w-40"
            placeholder={t('All classes')}
            value={state.filters.classId ?? ''}
            onChange={(e) => update({ classId: e.target.value })}
            options={(settings.data?.classes ?? []).map((c) => ({ value: c.classId, label: c.name }))}
          />
        </FilterBar>
        <DataTable
          caption={t('Applications')}
          columns={columns}
          rows={list.data?.data}
          getRowId={(a) => a.id}
          loading={list.isPending}
          error={list.error}
          onRetry={() => void list.refetch()}
          onRowClick={(a) => void navigate(`/admin/admissions/${a.id}`)}
          mobileCard={(a) => (
            <div className="flex min-w-0 items-center gap-3">
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium text-ink">{a.fullName}</p>
                <p className="truncate text-xs text-muted">
                  {a.reference} · {a.className} · {formatDateTime(a.submittedAt)}
                </p>
              </div>
              <Badge tone={admissionStatusTone[a.status]} size="sm">
                {t(admissionStatusLabel[a.status])}
              </Badge>
            </div>
          )}
          empty={
            <EmptyState
              icon={<FilePen />}
              title={t('No applications here')}
              description={t('Applications sent through the online form appear here.')}
              compact
            />
          }
        />
        <Pagination
          meta={list.data?.meta}
          onPageChange={setPage}
          onPerPageChange={(n) => update({ perPage: n })}
        />
      </Card>
    </>
  )
}
